import { useNavigate } from "react-router-dom";
import { getActionUrl } from "../../../app/draftHelper";
import { handleEnterSpaceKey } from "../../../app/keyboardUtils";
import { useAppSelector } from "../../../app/hooks";
import { UserRole } from "../../../app/userSlice";
import { PeerReviewListItemViewModel } from "../../peerReviews/peerReviewsApiSlice";
import {
	HomeCoursePromptViewModel,
	ProgressCounts,
	WriterHomeSpaLessonsViewModel,
	WriterHomeSpaWritingViewModel,
} from "../homeApiSlice";
import AssignmentInfo from "./AssignmentInfo";
import ProgressSection from "./ProgressSection";
import StatusSection from "./StatusSection";

interface AssignmentCardProps {
	id: number;
	isWritingCard: boolean;
	isReviewCard: boolean;
	isLessonCard: boolean;
	title?: string;
	subTitle?: string;
	isAssigned?: boolean;
	coursePrompt?: HomeCoursePromptViewModel;
	writerEssay?: WriterHomeSpaWritingViewModel;
	writerReview?: PeerReviewListItemViewModel;
	writerLesson?: WriterHomeSpaLessonsViewModel;
	teacherProgressCounts?: ProgressCounts;
}

export default function AssignmentCard(props: AssignmentCardProps) {
	const { role } = useAppSelector((state) => state.user);
	const navigate = useNavigate();

	function handleOnClick() {
		if (role === UserRole.Writer) {
			if (props.isWritingCard && props.coursePrompt && props.writerEssay) {
				const url = getActionUrl(
					props.writerEssay.action,
					props.writerEssay.actionId,
					props.coursePrompt.promptId
				);
				if (url) navigate(url);
			} else if (props.isReviewCard && props.writerReview) {
				navigate(`/reviews/${props.writerReview.reviewId}`);
			} else if (props.isLessonCard && props.writerLesson) {
				navigate(`/lessons/${props.writerLesson.tutorialInfo.codeName}`);
			}
		}
		if (role === UserRole.Proctor) {
			if (props.isWritingCard && props.coursePrompt) {
				navigate("/writing", {
					state: {
						promptId: props.coursePrompt.promptId,
					},
				});
			} else if (props.isReviewCard) {
				if (props.teacherProgressCounts && props.teacherProgressCounts.assignedAmount > 0) {
					navigate("/reviews", {
						state: {
							promptId: props.id,
						},
					});
				}
			} else if (props.isLessonCard) {
				navigate("/lessons", {
					state: {
						id: props.id,
					},
				});
			}
		}
	}

	function getCursor() {
		const isClickable =
			!props.isReviewCard ||
			role === UserRole.Writer ||
			(props.teacherProgressCounts?.assignedAmount ?? 0) > 0;

		return isClickable ? "hover:cursor-pointer" : "";
	}

	return (
		<li
			className={`${props.isLessonCard || !props.coursePrompt?.dueDate ? "min-h-20" : "min-h-28"} ${getCursor()} border border-gray-200 bg-white mb-1 p-1 last:mb-0 shadow-sm sm:rounded-md assignment-card hover:bg-gray-50`}>
			<div
				tabIndex={0}
				onClick={handleOnClick}
				onKeyDown={(e) => handleEnterSpaceKey(e, handleOnClick)}
				className="px-6 py-3.5 flex items-center justify-between">
				<div className="w-[68%] 2xl:w-[70%]">
					<AssignmentInfo
						id={props.id}
						title={props.title ?? props.coursePrompt?.promptName}
						subText={props.subTitle ?? props.coursePrompt?.genre}
						dueDate={props.coursePrompt?.dueDate ?? undefined}
						isPastDue={props.coursePrompt?.isPastDue}
						allComplete={props.teacherProgressCounts?.allComplete ?? false}
						isAssigned={props.isAssigned ?? props.coursePrompt?.isAssigned ?? false}
						tooltipContent={props.isLessonCard ? "Assigned Lesson" : "Assigned Prompt"}
					/>
				</div>
				<div className="w-[32%] 2xl:w-[30%]">
					{props.teacherProgressCounts && role === UserRole.Proctor && (
						<ProgressSection
							id={props.id}
							started={props.teacherProgressCounts.startedAmount}
							completed={props.teacherProgressCounts.completedAmount}
							assigned={props.teacherProgressCounts.assignedAmount}
							allComplete={props.teacherProgressCounts.allComplete ?? false}
							isReviewsCard={props.isReviewCard}
							isLessonsCard={props.isLessonCard}
							promptId={props.coursePrompt?.promptId}
							allowReviews={
								(props.isReviewCard && props.coursePrompt?.allowReviews) ?? false
							}
						/>
					)}
					{role === UserRole.Writer && (
						<StatusSection
							isWritingCard={props.isWritingCard}
							isReviewsCard={props.isReviewCard}
							isLessonsCard={props.isLessonCard}
							essay={props.writerEssay}
							review={props.writerReview}
							lesson={props.writerLesson}
						/>
					)}
				</div>
			</div>
		</li>
	);
}
